/**
 * <certificate-overview> — product-code search box + results list.
 *
 * Suggestions: debounced calls to Shopify's own predictive search endpoint
 * (routes.predictive_search_url), rendered server-side by
 * sections/certificate-search-suggestions.liquid so each suggestion carries
 * the product's article code as a data attribute. Picking one (or just
 * typing/pasting a code and submitting, no suggestion required) triggers the
 * certificate lookup.
 *
 * Lookup: GET {middleware_api_base_url}/certificates?articleCode=<code>&page=1&pageSize=<n>
 * via window.AtomicApi (assets/api-client.js), which is same-origin through
 * the app proxy configured in theme settings (settings.middleware_api_base_url,
 * e.g. /apps/atomic on qual — see docs/pim-handoff.md for the proxy setup).
 *
 * Optional browse collection (section.settings.collection): server-rendered
 * Liquid + {% paginate %}, no JS involved in rendering it. This class only
 * toggles its visibility — hidden while a search is active (search()),
 * restored when the input is cleared back to empty (reset(), via onInput()).
 */
class CertificateOverview extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-cert-form]');
    this.input = this.querySelector('[data-cert-input]');
    this.suggestionsList = this.querySelector('[data-cert-suggestions]');
    this.loading = this.querySelector('.certificate-overview__loading');
    this.hint = this.querySelector('[data-cert-hint]');
    this.collectionGrid = this.querySelector('[data-cert-collection]');
    this.resultsCount = this.querySelector('[data-cert-results-count]');
    this.list = this.querySelector('[data-cert-list]');
    this.noResults = this.querySelector('[data-cert-no-results]');
    this.noResultsText = this.querySelector('[data-cert-no-results-text]');
    this.errorBox = this.querySelector('[data-cert-error]');
    this.errorText = this.querySelector('[data-cert-error-text]');
    this.itemTemplate = this.querySelector('[data-cert-item-template]');

    this.suggestionsUrl = this.dataset.suggestionsUrl || '';
    this.pageSize = parseInt(this.dataset.pageSize, 10) || 20;
    this.prefetchAll = this.dataset.prefetchAll === 'true';
    this.allCertificatesPromise = null;
    this.resultsSingularTemplate = this.dataset.resultsSingularTemplate || '1 result';
    this.resultsPluralTemplate = this.dataset.resultsPluralTemplate || '[count] results';
    this.noResultsTemplate = this.dataset.noResultsTemplate || 'No results for "[terms]".';
    this.errorTemplate = this.dataset.errorTemplate || 'Something went wrong. Please try again in a moment.';
    this.downloadLabelTemplate = this.dataset.downloadLabelTemplate || 'Download [title]';

    this.suggestionsAbortController = null;
    this.suggestionsDebounce = null;
  }

  connectedCallback() {
    if (!this.form || !this.input) return;

    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.suggestionsList.addEventListener('click', this.onSuggestionClick.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));

    if (this.prefetchAll) this.prefetchAllCertificates();
  }

  // Fires once on connect when section.settings.prefetch_all is enabled — loads the
  // full certificate list in the background so search() can filter it client-side
  // instead of calling the middleware per search.
  prefetchAllCertificates() {
    if (!window.AtomicApi) return;

    this.allCertificatesPromise = window.AtomicApi.get('/certificates').then((res) => {
      if (!res.ok) return [];
      return (res.data && res.data.certificates) || [];
    });
  }

  isUkMarket() {
    return Boolean(window.theme && window.theme.market && window.theme.market.handle === 'gb');
  }

  // UK-only files are named with a "_UK_" marker in fileName; hide them outside the UK market.
  filterByMarket(certificates) {
    if (this.isUkMarket()) return certificates;
    return certificates.filter((certificate) => !(certificate.fileName || '').includes('_UK_'));
  }

  getArticleCodes(certificate) {
    return certificate.productArticleCodes || (certificate.fields && certificate.fields.ProductArticleCodes) || [];
  }

  matchesArticleCode(certificate, code) {
    const needle = code.toLowerCase();
    return this.getArticleCodes(certificate).some((articleCode) => String(articleCode).toLowerCase().includes(needle));
  }

  onInput() {
    const term = this.input.value.trim();

    clearTimeout(this.suggestionsDebounce);

    if (term === '') {
      this.closeSuggestions();
      this.reset();
      return;
    }

    if (term.length < 2) {
      this.closeSuggestions();
      return;
    }

    this.suggestionsDebounce = setTimeout(() => this.fetchSuggestions(term), 250);
  }

  // Restores the pre-search default view (hint + browse collection, if
  // configured) — mirrors the state a fresh page load starts in.
  reset() {
    this.setLoading(false);
    this.hint.hidden = false;
    this.resultsCount.hidden = true;
    this.noResults.hidden = true;
    this.errorBox.hidden = true;
    this.list.innerHTML = '';
    if (this.collectionGrid) this.collectionGrid.hidden = false;
  }

  onKeydown(event) {
    if (this.suggestionsList.hidden) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveSuggestion(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveSuggestion(-1);
        break;
      case 'Escape':
        this.closeSuggestions();
        break;
      case 'Enter': {
        const selected = this.suggestionsList.querySelector('[aria-selected="true"]');
        if (selected) {
          event.preventDefault();
          this.selectSuggestion(selected);
        }
        break;
      }
    }
  }

  onFocusOut(event) {
    if (this.contains(event.relatedTarget)) return;
    this.closeSuggestions();
  }

  onSuggestionClick(event) {
    const suggestion = event.target.closest('[data-cert-suggestion]');
    if (suggestion) this.selectSuggestion(suggestion);
  }

  selectSuggestion(suggestion) {
    this.input.value = suggestion.dataset.code || '';
    this.closeSuggestions();
    this.search(this.input.value.trim());
  }

  moveSuggestion(direction) {
    const options = Array.from(this.suggestionsList.querySelectorAll('[data-cert-suggestion]'));
    if (!options.length) return;

    const currentIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    let nextIndex = currentIndex + direction;
    if (nextIndex < 0) nextIndex = options.length - 1;
    if (nextIndex >= options.length) nextIndex = 0;

    options.forEach((option) => option.setAttribute('aria-selected', 'false'));
    options[nextIndex].setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', options[nextIndex].id);
  }

  fetchSuggestions(term) {
    if (!this.suggestionsUrl) return;

    if (this.suggestionsAbortController) this.suggestionsAbortController.abort();
    this.suggestionsAbortController = new AbortController();

    const url = `${this.suggestionsUrl}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=5&section_id=certificate-search-suggestions`;

    fetch(url, { signal: this.suggestionsAbortController.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((text) => {
        const markup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-certificate-search-suggestions');
        // certificate-search-suggestions.liquid wraps its <li role="option"> items in its
        // own <ul role="listbox">. Only pull that list's children in — dropping the whole
        // fragment's innerHTML here would nest a second listbox inside this.suggestionsList
        // (already a <ul role="listbox">), breaking the combobox/listbox ARIA structure.
        const list = markup ? markup.querySelector('ul') : null;
        this.renderSuggestions(list ? list.innerHTML : '');
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        this.closeSuggestions();
      });
  }

  renderSuggestions(markup) {
    this.suggestionsList.innerHTML = markup;
    const hasSuggestions = Boolean(this.suggestionsList.querySelector('[data-cert-suggestion]'));

    this.suggestionsList.hidden = !hasSuggestions;
    this.input.setAttribute('aria-expanded', hasSuggestions ? 'true' : 'false');
  }

  closeSuggestions() {
    this.suggestionsList.hidden = true;
    this.suggestionsList.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  onSubmit(event) {
    event.preventDefault();
    this.closeSuggestions();
    this.search(this.input.value.trim());
  }

  search(code) {
    if (!code) return;

    this.setLoading(true);
    this.hint.hidden = true;
    this.resultsCount.hidden = true;
    this.noResults.hidden = true;
    this.errorBox.hidden = true;
    if (this.collectionGrid) this.collectionGrid.hidden = true;

    if (this.prefetchAll && this.allCertificatesPromise) {
      this.allCertificatesPromise.then((certificates) => {
        this.setLoading(false);
        const matches = certificates.filter((certificate) => this.matchesArticleCode(certificate, code));
        this.renderResults(this.filterByMarket(matches), code);
      });
      return;
    }

    if (!window.AtomicApi) {
      this.setLoading(false);
      this.showError();
      return;
    }

    const path = `/certificates?articleCode=${encodeURIComponent(code)}&page=1&pageSize=${this.pageSize}`;

    window.AtomicApi.get(path).then((res) => {
      this.setLoading(false);

      if (!res.ok) {
        this.showError();
        return;
      }

      const certificates = (res.data && res.data.certificates) || [];
      this.renderResults(this.filterByMarket(certificates), code);
    });
  }

  setLoading(isLoading) {
    if (this.loading) this.loading.classList.toggle('hidden', !isLoading);
  }

  showError() {
    this.list.innerHTML = '';
    this.errorText.textContent = this.errorTemplate;
    this.errorBox.hidden = false;
  }

  renderResults(certificates, term) {
    this.list.innerHTML = '';

    this.resultsCount.hidden = false;
    this.resultsCount.textContent =
      certificates.length === 1
        ? this.resultsSingularTemplate
        : this.resultsPluralTemplate.replace('[count]', certificates.length);

    if (!certificates.length) {
      this.noResultsText.textContent = this.noResultsTemplate.replace('[terms]', term);
      this.noResults.hidden = false;
      return;
    }

    certificates.forEach((certificate) => {
      this.list.appendChild(this.buildItem(certificate, term));
    });
  }

  buildItem(certificate, term) {
    const node = this.itemTemplate.content.firstElementChild.cloneNode(true);
    const title = (certificate.fields && certificate.fields.DisplayTitle) || certificate.documentType || term;
    const url = certificate.uri || '';
    const codes = this.getArticleCodes(certificate);

    node.querySelector('[data-cert-item-title]').textContent = title;
    node.querySelector('[data-cert-item-code]').textContent = codes.length ? codes.join(', ') : term;

    const download = node.querySelector('[data-cert-item-download]');
    if (url) {
      download.href = url;
      download.setAttribute('aria-label', this.downloadLabelTemplate.replace('[title]', title));
    } else {
      download.removeAttribute('href');
      download.classList.add('certificate-overview__download--disabled');
      download.setAttribute('aria-hidden', 'true');
      download.setAttribute('tabindex', '-1');
    }

    return node;
  }
}

customElements.define('certificate-overview', CertificateOverview);
